import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  IconButton,
  useMediaQuery,
  useTheme,
  MenuItem,
  Chip
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import axiosApi from "../Utils/axiosApi";

/* ---------- Constants ---------- */

const emptyRow = {
  id: null,
  billingMonth: "",
  internet: "",
  water: "",
  electricity: "",
  food: "",
  miscellaneous: "",
  isNew: true,
  isDirty: true
};

const amountInputProps = {
  inputProps: { max: 9999999, min: 0 }
};

const inputHeightProps = {
  InputProps: { sx: { height: 40 } }
};

/* ---------- Component ---------- */

const UtilitySharedExpenseDialog = ({ open, onClose, hostelList }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [hostelId, setHostelId] = useState("");
  const [monthlyExpenses, setMonthlyExpenses] = useState([{ ...emptyRow }]);
  const [message, setMessage] = useState(null);
  const [foodCostMode, setFoodCostMode] = useState(null);

  const isFoodEnabled = foodCostMode === "FOOD_DIVIDING_SYSTEM";

  /* ---------- Helpers ---------- */

  const updateRow = (index, field, value) => {
    const updated = [...monthlyExpenses];
    updated[index][field] = value;
    updated[index].isDirty = true;
    setMonthlyExpenses(updated);
  };

  const addMonthRow = () => {
    setMonthlyExpenses([...monthlyExpenses, { ...emptyRow }]);
  };

  const resetDialogState = () => {
    setHostelId("");
    setMonthlyExpenses([{ ...emptyRow }]);
    setMessage(null);
    setFoodCostMode(null);
  };

  useEffect(() => {
    if (open) resetDialogState();
  }, [open]);

  /* ---------- FETCH ---------- */

  const fetchSharedUtilityExpenses = async (selectedHostelId) => {
    try {
      const response = await axiosApi.post(
        "/sharedUtilityExpense/getSharedUtilityExpense",
        { hostelIds: [selectedHostelId] }
      );

      const rowsFromDb = response.data.map((item) => ({
        id: item.id,
        billingMonth: item.billingMonth,
        internet: item.internetAmount ?? "",
        water: item.waterAmount ?? "",
        electricity: item.electricityAmount ?? "",
        food: item.foodAmount ?? "",
        miscellaneous: item.miscAmount ?? "",
        isNew: false,
        isDirty: false
      }));

      setMonthlyExpenses(rowsFromDb.length ? rowsFromDb : [{ ...emptyRow }]);
    } catch {
      setMessage({ type: "error", text: "Failed to fetch utility expenses" });
    }
  };

  const fetchHostelBillingMode = useCallback(async (hostelId) => {
    if (!hostelId) return;

    try {
      const response = await axiosApi.post(
        "/hostelerBillingConfiguration/getHostelBillingMode",
        { hostelIds: [hostelId] }
      );

      setFoodCostMode(response.data);
    } catch (error) {
      console.error("Failed to fetch billing mode", error);
      setFoodCostMode("FOOD_PER_MONTH");
    }
  }, []);

  /* ---------- SAVE ---------- */

  const handleSaveRow = async (index) => {
    if (!hostelId) {
      setMessage({ type: "error", text: "Please select a hostel" });
      return;
    }

    const row = monthlyExpenses[index];

    if (!row.billingMonth) {
      setMessage({ type: "error", text: "Billing month is required" });
      return;
    }

    const dto = {
      id: row.id,
      hostelId,
      billingMonth: row.billingMonth,
      internetAmount: Number(row.internet) || 0,
      waterAmount: Number(row.water) || 0,
      electricityAmount: Number(row.electricity) || 0,
      foodAmount: isFoodEnabled ? Number(row.food) || 0 : 0,
      miscAmount: Number(row.miscellaneous) || 0
    };

    try {
      const response = row.id
        ? await axiosApi.post(
            "/sharedUtilityExpense/updateSharedUtilityExpense",
            { sharedUtilityExpenseDTO: dto }
          )
        : await axiosApi.post(
            "/sharedUtilityExpense/createSharedUtilityExpense",
            { sharedUtilityExpenseDTO: dto }
          );

      const updated = [...monthlyExpenses];
      updated[index] = {
        ...row,
        id: response?.data?.id ?? row.id,
        isNew: false,
        isDirty: false
      };

      setMonthlyExpenses(updated);
      setMessage({
        type: "success",
        text: row.id ? "Expense updated" : "Expense created"
      });
    } catch {
      setMessage({ type: "error", text: "Save failed" });
    }
  };

  /* ---------- DELETE ---------- */

  const handleDeleteRow = async (index) => {
    const row = monthlyExpenses[index];

    if (row.isNew) {
      setMonthlyExpenses(monthlyExpenses.filter((_, i) => i !== index));
      return;
    }

    try {
      await axiosApi.delete("/sharedUtilityExpense/deleteSharedUtilityExpense", {
        data: { hostelId, billingMonth: row.billingMonth }
      });

      setMonthlyExpenses(monthlyExpenses.filter((_, i) => i !== index));
      setMessage({ type: "success", text: "Deleted successfully" });
    } catch {
      setMessage({ type: "error", text: "Delete failed" });
    }
  };

  /* ---------- UI ---------- */

  return (
    <Dialog
      open={open}
      onClose={(_, r) => r !== "backdropClick" && onClose()}
      fullScreen={isMobile}
      fullWidth
      maxWidth={false}
    >
      <DialogTitle>Utility & Shared Expenses</DialogTitle>

      <DialogContent>
        {message && <Alert severity={message.type}>{message.text}</Alert>}

        {!isFoodEnabled && foodCostMode && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Food expense is calculated monthly. Food amount entry is disabled.
          </Alert>
        )}

        {/* Hostel Select */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              size="small"
              label="Hostel"
              value={hostelId}
              onChange={(e) => {
                const id = e.target.value;
                setHostelId(id);
                fetchSharedUtilityExpenses(id);
                fetchHostelBillingMode(id);
              }}
            >
              {hostelList.map((h) => (
                <MenuItem key={h.id} value={h.id}>
                  {h.hostelName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} textAlign="right">
            <Button startIcon={<AddIcon />} size="small" onClick={addMonthRow}>
              Add Month
            </Button>
          </Grid>
        </Grid>

        {/* Rows */}
        {monthlyExpenses.map((row, index) => (
          <Grid container spacing={1} key={index} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={2}>
              <TextField
                type="month"
                size="small"
                fullWidth
                value={row.billingMonth}
                onChange={(e) =>
                  updateRow(index, "billingMonth", e.target.value)
                }
              />
              {row.isNew && <Chip label="New" size="small" color="success" />}
              {!row.isNew && row.isDirty && (
                <Chip label="Edited" size="small" color="warning" />
              )}
            </Grid>

            {["internet", "water", "electricity", "food"].map((f) => (
              <Grid item xs={6} sm={2} key={f}>
                <TextField
                  label={f}
                  type="number"
                  size="small"
                  fullWidth
                  {...amountInputProps}
                  {...inputHeightProps}
                  value={row[f]}
                  disabled={f === "food" && !isFoodEnabled}
                  onChange={(e) => updateRow(index, f, e.target.value)}
                />
              </Grid>
            ))}

            <Grid item xs={6} sm={1}>
              <TextField
                label="Misc"
                type="number"
                size="small"
                fullWidth
                {...amountInputProps}
                {...inputHeightProps}
                value={row.miscellaneous}
                onChange={(e) =>
                  updateRow(index, "miscellaneous", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} sm={1}>
              <IconButton
                onClick={() => handleSaveRow(index)}
                disabled={!row.isDirty}
              >
                <SaveIcon />
              </IconButton>
              <IconButton onClick={() => handleDeleteRow(index)}>
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        ))}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => {
            resetDialogState();
            onClose();
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UtilitySharedExpenseDialog;
